import { useMemo, useState } from 'react';
import { useReglages } from '../../api/reglages.jsx';
import { envoisApi } from '../../api/resources';
import { Bar } from '../../components/ui/Bar.jsx';
import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { Inp } from '../../components/ui/Inputs.jsx';
import { DEVISE, fmt } from '../../lib/currency';
import { CLE_COMPTABLE } from '../../lib/cloture';
import { tr, trf } from '../../lib/i18n';
import { C } from '../../theme/palette';

/** Le serveur prend trente factures au plus par e-mail. */
export const MAX_FACTURES_COMPTABLE = 30;

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** « 31.12.2026 » → « 2026-12-31 », pour trier les plus récentes en haut. */
const triable = (date) =>
  String(date || '')
    .split('.')
    .reverse()
    .join('-');

/**
 * Envoie des factures choisies à la comptable ou à la fiduciaire : un seul
 * e-mail, le PDF de chaque facture joint. Elle n'a pas besoin d'un compte
 * SwissTech. Son adresse se reprend de Compta → Comptable / Fiduciaire, et
 * celle tapée ici y est gardée pour le prochain envoi.
 *
 * @param {object} props propriétés
 * @param {object[]} props.factures factures de la liste
 * @param {object[]} props.clients clients, pour le nom sur chaque ligne
 * @param {(f: object) => {ttc: number}} props.calc totaux d'une facture
 * @returns {JSX.Element} le bouton, puis le choix des factures
 */
export function EnvoiComptable({ factures, clients, calc }) {
  const { reglageJson, setReglageJson } = useReglages();
  const comptable = reglageJson(CLE_COMPTABLE, null);
  const [ouvert, setOuvert] = useState(false);
  const [email, setEmail] = useState('');
  const [annee, setAnnee] = useState('tous');
  const [choix, setChoix] = useState([]);
  const [etat, setEtat] = useState('repos');
  const [message, setMessage] = useState('');

  // Seules les factures émises partent : un brouillon n'a pas encore de PDF.
  const emises = useMemo(
    () =>
      factures
        .filter((f) => Number.isInteger(f.id) && f.statut !== 'Brouillon' && !f.recur)
        .sort((a, b) => triable(b.date).localeCompare(triable(a.date))),
    [factures],
  );
  const annees = [
    ...new Set(emises.map((f) => String(f.date || '').split('.')[2]).filter(Boolean)),
  ];
  const liste = emises.filter(
    (f) => annee === 'tous' || String(f.date || '').split('.')[2] === annee,
  );
  const coches = choix.filter((id) => liste.some((f) => f.id === id));
  const toutCoche = liste.length > 0 && coches.length === liste.length;

  const ouvre = () => {
    setEmail(comptable?.email || '');
    setChoix([]);
    setEtat('repos');
    setMessage('');
    setOuvert(true);
  };

  const bascule = (id) =>
    setChoix((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const adresse = email.trim();
  const tropDeFactures = coches.length > MAX_FACTURES_COMPTABLE;
  const pret = coches.length > 0 && !tropDeFactures && EMAIL.test(adresse) && etat !== 'envoi';

  const envoie = async () => {
    setEtat('envoi');
    setMessage('');
    try {
      const ligne = await envoisApi.envoieAuComptable(adresse, coches);
      // L'adresse reste dans la fiche de la comptable, pour la prochaine fois.
      if ((comptable?.email || '') !== adresse) {
        setReglageJson(CLE_COMPTABLE, { ...(comptable || {}), email: adresse });
      }
      setEtat('envoye');
      setMessage(
        trf('{0} facture(s) envoyée(s) à {1}.', coches.length, ligne?.destinataire || adresse),
      );
      setChoix([]);
    } catch (e) {
      setEtat('echec');
      setMessage(e?.message || tr('L’envoi a échoué.'));
    }
  };

  if (!ouvert) {
    return (
      <div style={{ marginBottom: 11 }}>
        <Btn label="📤 Envoyer à ma comptable" color={C.navy} ghost full onClick={ouvre} />
      </div>
    );
  }

  return (
    <Card style={{ marginBottom: 11 }}>
      <Bar
        icon="📤"
        title="Envoyer à ma comptable"
        sub="Un e-mail avec le PDF de chaque facture, sans compte SwissTech"
        color={C.navy}
      />
      <div style={{ padding: 13 }}>
        <Inp
          label="E-mail de la comptable / fiduciaire"
          type="email"
          value={email}
          placeholder="comptable@fiduciaire.ch"
          onChange={(e) => setEmail(e.target.value)}
        />
        {!comptable?.email && (
          <div style={{ fontSize: 10.5, color: C.muted, margin: '-4px 0 10px', lineHeight: 1.45 }}>
            {tr('L’adresse sera gardée pour le prochain envoi.')}
          </div>
        )}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
          {['tous', ...annees].map((a) => (
            <div
              key={a}
              role="button"
              onClick={() => setAnnee(a)}
              style={{
                fontSize: 10.5,
                fontWeight: 800,
                padding: '5px 10px',
                borderRadius: 14,
                cursor: 'pointer',
                border: '1px solid ' + (annee === a ? C.navy : C.border),
                background: annee === a ? C.navy : C.white,
                color: annee === a ? '#fff' : C.ink,
              }}
            >
              {a === 'tous' ? tr('Toutes') : a}
            </div>
          ))}
        </div>
        {liste.length === 0 ? (
          <div style={{ fontSize: 11.5, color: C.muted, padding: '8px 0' }}>
            {tr('Aucune facture envoyée à choisir.')}
          </div>
        ) : (
          <>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 11.5,
                fontWeight: 800,
                color: C.navy,
                padding: '6px 2px',
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={toutCoche}
                onChange={() =>
                  setChoix((p) =>
                    toutCoche
                      ? p.filter((id) => !liste.some((f) => f.id === id))
                      : [...new Set([...p, ...liste.map((f) => f.id)])],
                  )
                }
              />
              {tr('Tout cocher')}
            </label>
            <div
              style={{
                border: '1px solid ' + C.border,
                borderRadius: 10,
                maxHeight: 320,
                overflowY: 'auto',
              }}
            >
              {liste.map((f) => {
                const cl = clients.find((c) => c.id === f.clientId);
                return (
                  <label
                    key={f.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 9,
                      padding: '9px 10px',
                      borderBottom: '1px solid ' + C.border,
                      cursor: 'pointer',
                      background: choix.includes(f.id) ? C.navyL : C.white,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={choix.includes(f.id)}
                      onChange={() => bascule(f.id)}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 800, color: C.ink }}>
                        {f.num || f.id}
                        <span style={{ fontWeight: 600, color: C.muted }}> · {f.date}</span>
                      </div>
                      <div
                        style={{
                          fontSize: 10.5,
                          color: C.muted,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {cl?.nom || '—'} · {tr(f.statut)}
                      </div>
                    </div>
                    <div
                      style={{
                        fontSize: 11.5,
                        fontWeight: 800,
                        color: C.navy,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {DEVISE} {fmt(calc(f).ttc)}
                    </div>
                  </label>
                );
              })}
            </div>
          </>
        )}
        <div style={{ fontSize: 11, color: tropDeFactures ? C.red : C.muted, margin: '8px 0' }}>
          {tropDeFactures
            ? tr('Trente factures au plus par envoi')
            : trf('{0} facture(s) choisie(s)', coches.length)}
        </div>
        <Btn
          label={etat === 'envoi' ? '⏳ Envoi en cours…' : '📤 Envoyer à ma comptable'}
          color={C.navy}
          full
          disabled={!pret}
          onClick={envoie}
        />
        {message && (
          <div
            role="status"
            style={{
              fontSize: 11,
              fontWeight: 700,
              marginTop: 6,
              color: etat === 'echec' ? C.red : C.green,
            }}
          >
            {etat === 'echec' ? '✕ ' : '✓ '}
            {message}
          </div>
        )}
        <div style={{ marginTop: 8 }}>
          <Btn label="Fermer" color={C.grey} ghost full sm onClick={() => setOuvert(false)} />
        </div>
      </div>
    </Card>
  );
}
